import React, { useCallback, useEffect, useState } from "react";
import { Send, Trash2, Pencil, X, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

function fmtDT(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ClientNotesTab({ clientId }) {
    const { user: me } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [shared, setShared] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const isAdmin = me?.is_super_admin || me?.role === "admin";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/internal/clients/${clientId}/notes`);
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento note"); }
        setLoading(false);
    }, [clientId]);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!body.trim() || submitting) return;
        setSubmitting(true);
        try {
            if (editing) {
                await api.patch(`/internal/clients/${clientId}/notes/${editing.id}`, {
                    body: body.trim(),
                    visibility: shared ? "shared_with_client" : "internal_only",
                });
                toast.success("Nota aggiornata");
            } else {
                await api.post(`/internal/clients/${clientId}/notes`, {
                    body: body.trim(),
                    visibility: shared ? "shared_with_client" : "internal_only",
                });
                toast.success("Nota creata");
            }
            setBody(""); setShared(false); setEditing(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore salvataggio");
        } finally { setSubmitting(false); }
    };

    const startEdit = (n) => {
        setEditing(n); setBody(n.body); setShared(n.visibility === "shared_with_client");
    };
    const cancelEdit = () => { setEditing(null); setBody(""); setShared(false); };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/internal/clients/${clientId}/notes/${confirmDelete.id}`);
            toast.success("Nota eliminata");
            setConfirmDelete(null);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore eliminazione"); }
    };

    return (
        <div data-testid="tab-note">
            <h2 className="lt-section-title" style={{ marginBottom: 16 }}>Note interne</h2>
            <form onSubmit={submit} className="lt-card" style={{ padding: 16, marginBottom: 16 }} data-testid="note-form">
                <textarea
                    className="lt-textarea"
                    rows={3}
                    placeholder="Scrivi una nota…"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={4000}
                    data-testid="note-form-body"
                    style={{ width: "100%", marginBottom: 10 }}
                />
                <div className="flex items-center justify-between">
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "#3A3F3A" }}>
                        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="note-form-shared" />
                        <span>Condividi con il cliente (visibile dal Portale)</span>
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                        {editing && <button type="button" className="lt-btn-secondary" onClick={cancelEdit}>Annulla</button>}
                        <button type="submit" className="lt-btn-primary" disabled={!body.trim() || submitting} data-testid="note-form-submit">
                            <Send size={14} /> {editing ? "Aggiorna" : "Pubblica"}
                        </button>
                    </div>
                </div>
            </form>
            {loading ? <div style={{ padding: 24, color: "#8A8F88" }}>Caricamento…</div> :
             items.length === 0 ? <div style={{ padding: 24, color: "#8A8F88", textAlign: "center" }}>Nessuna nota ancora.</div> :
             <div style={{ display: "grid", gap: 10 }} data-testid="notes-list">
                {items.map((n) => {
                    const isMeeting = n.source === "meeting";
                    return (
                    <div key={n.id} className="lt-card" style={{ padding: 16, borderLeft: isMeeting ? "3px solid #3B82F6" : undefined }} data-testid={`note-row-${n.id}`}>
                        <div className="flex items-start justify-between" style={{ gap: 12, marginBottom: 6 }}>
                            <div>
                                <div style={{ fontWeight: 600, color: "#181B20", fontSize: 13.5 }}>{n.author_name || "—"}</div>
                                <div style={{ fontSize: 12, color: "#8A8F88" }}>{fmtDT(n.created_at)}</div>
                                {isMeeting && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/app/riunioni/${n.meeting_id}/join`)}
                                        data-testid={`note-meeting-badge-${n.id}`}
                                        style={{
                                            marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
                                            padding: "3px 8px", background: "#DBEAFE", color: "#1E40AF",
                                            border: "none", borderRadius: 3, cursor: "pointer",
                                            fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5,
                                            letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 600,
                                        }}
                                        title={n.meeting_title || "Vai alla riunione"}
                                    >
                                        <Video size={11} />
                                        Dalla riunione del {fmtDate(n.meeting_date)}
                                    </button>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {isMeeting ? (
                                    <span style={{ padding: "3px 8px", background: "#EFEFEC", color: "#5F6560", borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>Riunione</span>
                                ) : n.visibility === "shared_with_client" ? (
                                    <span style={{ padding: "3px 8px", background: "#E5FF00", color: "#181B20", borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>Condivisa</span>
                                ) : (
                                    <span style={{ padding: "3px 8px", background: "#EFEFEC", color: "#5F6560", borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>Interna</span>
                                )}
                                {!isMeeting && (isAdmin || n.author_id === me?.id) && (
                                    <>
                                        <button className="lt-icon-btn" onClick={() => startEdit(n)} aria-label="Modifica" data-testid={`note-edit-${n.id}`}><Pencil size={14} /></button>
                                        <button className="lt-icon-btn" onClick={() => setConfirmDelete(n)} aria-label="Elimina" data-testid={`note-delete-${n.id}`}><Trash2 size={14} /></button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ fontSize: 14, color: "#181B20", whiteSpace: "pre-wrap" }}>{n.body}</div>
                    </div>
                    );
                })}
             </div>
            }
            {confirmDelete && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDelete(null)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(420px, 92vw)" }}>
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Eliminare nota?</h2><button className="lt-icon-btn" onClick={() => setConfirmDelete(null)}><X size={14} /></button></div>
                        <div style={{ padding: 20, fontSize: 14 }}>La nota verrà eliminata definitivamente.</div>
                        <div className="lt-form-actions" style={{ padding: "12px 20px" }}>
                            <div style={{ flex: 1 }} />
                            <button className="lt-btn-secondary" onClick={() => setConfirmDelete(null)}>Annulla</button>
                            <button onClick={doDelete} data-testid="note-delete-confirm" style={{ padding: "9px 16px", background: "#8C2C38", color: "#FFF", border: "none", borderRadius: 3, cursor: "pointer", fontWeight: 600 }}>Elimina</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
